import { useEffect, useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-heart-pulse-line',
    title: 'Sua produção não pode parar por dor nas costas',
    description: 'Cadeiras ergonômicas com base de aço reforçado e tecido de alta durabilidade. Costureiras que trabalham confortáveis produzem mais peças por turno — sem afastamentos por LER/DORT.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Conformidade com a NR-17',
    description: 'Nosso mobiliário segue as normas de ergonomia da NR-17. Assento anatômico, encosto com apoio lombar ajustável e altura regulável. Sua empresa em conformidade, sua equipe protegida.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Equipe própria de montagem',
    description: 'Não terceirizamos a montagem. Nossa equipe instala tudo no prazo combinado, sem atrasar sua produção. Você recebe o mobiliário pronto para usar no dia seguinte.',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Projeto 3D gratuito antes de comprar',
    description: 'Antes de fabricar qualquer peça, você aprova o projeto em 3D. Vê exatamente como vai ficar o layout da fábrica, a posição das bancadas e o espaço entre as máquinas.',
  },
];

const blocks = [
  {
    title: 'Cadeira Ergonômica que Aumenta a Produção de Peças por Dia',
    text: [
      'Uma costureira que sente dor nas costas às 14h produz metade do que produziria confortável até as 17h. A cadeira certa não é custo — é investimento direto na sua capacidade produtiva. Nossas cadeiras para facções têm base de aço com 5 pontas e rodízios de nylon resistente ao piso industrial, assento em espuma injetada de alta densidade (D45) que não afunda com o uso, encosto com regulagem de altura e apoio lombar ajustável, e braços 3D que acompanham o movimento dos cotovelos durante a costura.',
      'O resultado prático: costureiras que antes precisavam de pausas frequentes para alongar passam a trabalhar turnos completos sem desconforto. Fábricas que trocaram as cadeiras antigas relatam aumento de 15 a 20% na produção diária — sem contratar mais ninguém, sem comprar mais máquinas. Só com a cadeira certa.',
    ],
    img: '/media/ia/0293.webp',
    reverse: false,
  },
  {
    title: 'Bancadas e Mesas de Corte que Eliminam o Retrabalho',
    text: [
      'Uma mesa de corte na altura errada gera desperdício de tecido e dor no pescoço. Nossas bancadas são fabricadas com tampo de MDF de 40mm revestido em fórmica resistente a riscos, nas alturas corretas para trabalho em pé (90–95cm) ou sentado (72–76cm), com estrutura metálica tubular de 50x50mm que não vibra durante o corte.',
      'O espaço inferior é aproveitado com prateleiras para rolos de tecido, gavetas para moldes e nichos para caixas de aviamentos. Tudo dimensionado para o fluxo da sua produção: do corte à costura, da costura ao acabamento. Solicitamos uma visita técnica, medimos o espaço, entregamos o projeto 3D e você aprova antes de fabricarmos qualquer peça.',
    ],
    img: '/media/ia/0294.webp',
    reverse: true,
  },
  {
    title: 'Suporte Local em Caruaru e Todo o Agreste',
    text: [
      'Não somos uma loja virtual que entrega em caixa e você monta sozinho. Somos uma empresa local, com equipe própria de montagem, que atende Caruaru, Santa Cruz do Capibaribe, Toritama, Surubim e toda a região do Agreste Pernambucano. Quando algo precisa de ajuste, estamos a uma ligação de distância.',
      'Nosso processo é simples: você nos chama pelo WhatsApp, agendamos a visita técnica, medimos o espaço, entregamos o projeto 3D em até 48h, você aprova, fabricamos e instalamos. Sem surpresas, sem atrasos, sem você ter que parar a produção por mais de um dia.',
    ],
    img: '/media/ia/0295.webp',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: '/media/ia/0296.webp',
    alt: 'Cadeira ergonômica NR-17 para costureira',
  },
  {
    src: '/media/ia/0297.webp',
    alt: 'Mesa de corte industrial',
  },
  {
    src: '/media/ia/0298.webp',
    alt: 'Layout de fábrica com estações ergonômicas',
  },
  {
    src: '/media/ia/0299.webp',
    alt: 'Armário industrial para aviamentos',
  },
];

export default function CosturaPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Mobiliário Ergonômico NR-17 para Facções de Costura | Caruaru PE',
    description: 'Cadeiras ergonômicas NR-17, bancadas de corte e mobiliário industrial para facções de costura. Projeto 3D gratuito, equipe própria de montagem. Caruaru e Agreste PE.',
    keywords: 'cadeira ergonômica NR-17 costura Recife, mobiliário facção têxtil Petrolina, bancada corte facção Garanhuns, cadeira costureira Arcoverde, móveis indústria têxtil Sertão PE, cadeira facção Pernambuco, mobiliário costura Agreste, cadeira costureira Nordeste, bancada corte industrial Recife, cadeira ergonômica fábrica Petrolina, mobiliário facção Garanhuns, mobiliário costura Caruaru, cadeira costureira Bezerros',
    path: '/costura',
  });

  useSchemaLD({
    id: 'schema-costura',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mobiliário Ergonômico NR-17 para Facções de Costura | Caruaru PE',
      description: 'Cadeiras ergonômicas NR-17, bancadas de corte e mobiliário industrial para facções de costura. Projeto 3D gratuito, equipe própria de montagem.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/costura`,
      about: {
        '@type': 'Service',
        name: 'Mobiliário Ergonômico NR-17 para Facções de Costura',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      {/* Hero */}
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(/media/ia/0300.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Facções de Costura e Indústria Têxtil
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Sua produção não pode parar por dor nas costas da sua equipe.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Cadeiras ergonômicas com base de aço, conformidade com a <strong className="text-white">NR-17</strong> e bancadas de corte na altura certa. Mais peças por turno, menos afastamentos, mais lucro.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Equipe própria de montagem · Suporte local em Caruaru e Agreste
            </p>
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Fac%C3%A7%C3%B5es%20de%20Costura%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que as maiores facções do Agreste escolhem a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Blocks */}
      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Ergonomia Industrial</span> que Aumenta sua Produção
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Fac%C3%A7%C3%B5es%20de%20Costura%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-gray-900 text-3xl md:text-4xl font-bold mb-8">
            Mais de <span className="text-[#E8610A]">10 anos</span> equipando fábricas do Agreste
          </h2>
          <p className="text-gray-600 text-base leading-relaxed mb-5">
            A <strong>Home Office Premium</strong> é especialista em mobiliário industrial para o setor têxtil. Atendemos facções de costura em Caruaru, Santa Cruz do Capibaribe, Toritama e toda a região — com equipe própria de montagem e suporte local que você pode ligar quando precisar.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-10">
            Nosso processo garante zero surpresas: visita técnica, projeto 3D para aprovação, fabricação com materiais de primeira linha e instalação pela nossa equipe. Você não precisa se preocupar com nada — só com a produção.
          </p>
          <a
            href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Fac%C3%A7%C3%B5es%20de%20Costura%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 justify-center bg-[#E8610A] hover:bg-[#C4500A] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            FALAR COM UM ESPECIALISTA
          </a>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Oferta exclusiva</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">
            Projeto 3D do layout da sua fábrica, sem custo e sem compromisso.
          </h2>
          <p className="text-gray-400 text-base mb-10">
            Agende a visita técnica agora. Nossa equipe vai até sua fábrica, mede o espaço e entrega o projeto 3D em até 48h. Você aprova, a gente fabrica e instala.
          </p>
          <a
            href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Fac%C3%A7%C3%B5es%20de%20Costura%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO MEU PROJETO 3D GRATUITO
          </a>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">
              Soluções para <span className="text-[#E8610A]">Facções de Costura</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">
            SUPORTE LOCAL · CARUARU E AGRESTE PERNAMBUCANO
          </p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">
            Solicite seu orçamento
          </h2>
          <p className="text-gray-500 text-base mb-8">Projeto 3D gratuito incluso. Resposta em até 2 horas.</p>
          <a
            href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Fac%C3%A7%C3%B5es%20de%20Costura%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm leading-relaxed">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
              <img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" />
            </a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
              <img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" />
            </a>
          </div>
        </div>
      </footer>

      <a
        ref={whatsappBtnRef}
        href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Fac%C3%A7%C3%B5es%20de%20Costura%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200"
        aria-label="WhatsApp"
        style={{ display: 'flex' }}
      >
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
